import { createTheme } from '@material-ui/core/styles'
import { Color } from './color'
import { FontSize } from './font-size'
import { FontWeight } from './font-weight'
import { ScreenSize } from './screen-size'

const interFont = ['Inter', 'sans-serif'].join(',')

const breakpointOptions = createTheme({
  breakpoints: {
    values: {
      xs: ScreenSize.EXTRA_SMALL,
      sm: ScreenSize.SMALL,
      md: ScreenSize.MEDIUM,
      lg: ScreenSize.LARGE,
      xl: ScreenSize.EXTRA_LARGE,
    },
  },
})

const typographyOptions = createTheme(breakpointOptions, {
  typography: {
    h1: {
      color: Color.BLACK_95,
      fontFamily: interFont,
      fontSize: FontSize.PX_49,
      fontWeight: FontWeight.BOLD,
      letterSpacing: 0,
      lineHeight: 1.2,
      margin: `0 0 ${FontSize.PX_16}`,
      [breakpointOptions.breakpoints.up('md')]: {
        fontSize: FontSize.PX_61,
      },
    },
    h2: {
      color: Color.BLACK_95,
      fontFamily: interFont,
      fontSize: FontSize.PX_39,
      fontWeight: FontWeight.BOLD,
      letterSpacing: 0,
      lineHeight: 1.2,
      margin: `0 0 ${FontSize.PX_16}`,
      [breakpointOptions.breakpoints.up('md')]: {
        fontSize: FontSize.PX_49,
      },
    },
    h3: {
      color: Color.BLACK_95,
      fontFamily: interFont,
      fontSize: FontSize.PX_31,
      fontWeight: FontWeight.BOLD,
      letterSpacing: 0,
      lineHeight: 1.2,
      margin: `0 0 ${FontSize.PX_16}`,
      [breakpointOptions.breakpoints.up('md')]: {
        fontSize: FontSize.PX_39,
      },
    },
    h4: {
      color: Color.BLACK_95,
      fontFamily: interFont,
      fontSize: FontSize.PX_25,
      fontWeight: FontWeight.BOLD,
      letterSpacing: 0,
      lineHeight: 1.2,
      margin: `0 0 ${FontSize.PX_16}`,
      [breakpointOptions.breakpoints.up('md')]: {
        fontSize: FontSize.PX_31,
      },
    },
    h5: {
      color: Color.BLACK_95,
      fontFamily: interFont,
      fontSize: FontSize.PX_20,
      fontWeight: FontWeight.BOLD,
      letterSpacing: 0,
      lineHeight: 1.2,
      margin: `0 0 ${FontSize.PX_16}`,
      [breakpointOptions.breakpoints.up('md')]: {
        fontSize: FontSize.PX_25,
      },
    },
    h6: {
      color: Color.BLACK_95,
      fontFamily: interFont,
      fontSize: FontSize.PX_16,
      fontWeight: FontWeight.BOLD,
      letterSpacing: 0,
      lineHeight: 1.2,
      margin: `0 0 ${FontSize.PX_16}`,
    },
    subtitle1: {
      color: Color.BLACK_95,
      fontFamily: interFont,
      fontSize: FontSize.PX_18,
      fontWeight: FontWeight.BOLD,
      letterSpacing: 0,
      lineHeight: 1.4,
      margin: `0 0 ${FontSize.PX_14}`,
    },
    subtitle2: {
      color: Color.BLACK_95,
      fontFamily: interFont,
      fontSize: FontSize.PX_16,
      fontWeight: FontWeight.BOLD,
      letterSpacing: 0,
      lineHeight: 1.4,
      margin: `0 0 ${FontSize.PX_12}`,
    },
    body1: {
      color: Color.BLACK_60,
      fontFamily: interFont,
      fontSize: FontSize.PX_20,
      letterSpacing: 0,
      lineHeight: 1.2,
      margin: `0 0 ${FontSize.PX_16}`,
    },
    body2: {
      color: Color.BLACK_60,
      fontFamily: interFont,
      fontSize: FontSize.PX_16,
      letterSpacing: 0,
      lineHeight: 1.2,
      margin: `0 0 ${FontSize.PX_16}`,
    },
    button: {
      color: Color.BLACK_95,
      fontFamily: interFont,
    },
    caption: {
      color: Color.BLACK_95,
      fontFamily: interFont,
    },
    overline: {
      color: Color.BLACK_95,
      fontFamily: interFont,
    },
  },
})

const componentOptions = createTheme(typographyOptions, {
  props: {
    MuiGrid: {
      spacing: 3,
    },
  },
  overrides: {
    MuiGrid: {
      container: {
        // 16px margins accounting for -12px of padding on the column elements
        paddingLeft: '4px!important',
        paddingRight: '4px!important',
        [typographyOptions.breakpoints.up('sm')]: {
          // 32px margins accounting for -12px of padding on the column elements
          paddingLeft: '20px!important',
          paddingRight: '20px!important',
        },
        [typographyOptions.breakpoints.up('md')]: {
          // 64px margins accounting for -12px of padding on the column elements
          paddingLeft: '52px!important',
          paddingRight: '52px!important',
        },
      },
    },
  },
})

export const defaultTheme = createTheme(componentOptions, {
  overrides: {
    MuiCssBaseline: {
      '@global': {
        '.body3': {
          color: Color.BLACK_60,
          fontSize: FontSize.PX_13,
          lineHeight: 1.2,
          margin: `0 0 ${FontSize.PX_16}`,
        },
        '.h7': {
          color: Color.BLACK_95,
          fontSize: FontSize.PX_13,
          fontWeight: FontWeight.BOLD,
          letterSpacing: 0,
          lineHeight: 1.2,
          margin: `0 0 ${FontSize.PX_16}`,
        },
        '.subhead': {
          color: Color.BLACK_95,
          fontWeight: FontWeight.BOLD,
          letterSpacing: 0,
          lineHeight: 1.4,
          margin: `${FontSize.PX_16} 0`,
        },
        '.subhead.large': {
          fontSize: FontSize.PX_18,
        },
        '.subhead.medium': {
          fontSize: FontSize.PX_16,
        },
        '.subhead.small': {
          fontSize: FontSize.PX_14,
        },
        '.subhead.xsmall': {
          fontSize: FontSize.PX_12,
        },
        '.body.large': {
          fontSize: FontSize.PX_18,
        },
        '.body.medium': {
          fontSize: FontSize.PX_16,
        },
        '.body.small': {
          fontSize: FontSize.PX_14,
          lineHeight: 1.4,
        },
        '.body.xsmall': {
          fontSize: FontSize.PX_12,
          lineHeight: 1.4,
        },
        strong: {
          fontWeight: FontWeight.MEDIUM,
        },
        'p a': {
          color: Color.PRIMARY_BLUE,
        },
      },
    },
  },
})
